/**
 * Rewriting an English-spelled sound into the pinyin that spells the same sound.
 *
 * This is the join between the two halves of the problem, and it took a run of the harness to
 * see that it was the missing piece. The confusion matrix is about *consonants* — it knows
 * zh/z and n/l are the same sound. But a recogniser working in English does not write pinyin
 * vowels at all: it writes "zee", "chow", "way", "goo", because those are how English spells
 * [i], [au], [ei], [u]. Feed "zee" to a pinyin segmenter and it fails outright — `ee` is not
 * a final — so every downstream layer falls back to letter distance and the syllable work
 * never fires.
 *
 * So each spoken word is expanded into the pinyin spellings it might be, and the scorer takes
 * whichever fits the roster. Expansion rather than replacement, because these rules are
 * guesses: "ow" is 玫 -ao in "chow" and -ou in "know", and only the roster knows which.
 */

/** English spelling → the pinyin finals it might be. Longest first. */
const REWRITES: [RegExp, string[]][] = [
  [/ee/g, ['i']],
  [/oo/g, ['u']],
  [/ay/g, ['ei']],
  [/aw/g, ['ao']],
  // English has no way to spell the -ian final, so a recogniser reaches for the nearest
  // English word it knows: 錢/Qian said aloud comes back as "chair", 田/Tian as "tear".
  // Found in the yard, on a real pickup line, not in the fixtures — which is the point
  // of writing it down here rather than adding one more fixture.
  [/air/g, ['ian', 'ie']],
  // "eo" is not a pinyin final at all, so a word carrying it has no reading and the syllable
  // layer goes silent on it. It is how English spells the -iu of 刘 Liu — Leo, Leow, Teo —
  // so the expansion gives that layer something to read. A child actually named Leo still
  // matches "leo" exactly, which outscores the rewritten reading.
  [/eo/g, ['iu']],
  // The palatal respelling rule. Pinyin never writes zh/ch/sh or z/c/s before an -i- glide:
  // that series is spelled j/q/x there instead, which is why "qian" exists and "chian" does
  // not. A recogniser working from sound writes the retroflex it hears, so the rewrite above
  // lands on an illegal spelling and the segmenter — correctly — refuses it. Respelling to
  // the palatal series is what turns a heard "chian" into the "qian" the roster holds, and it
  // generalises: chiang→qiang, shiao→xiao, chieh→qie.
  [/zhi(?=[aeo])/g, ['ji']],
  [/chi(?=[aeo])/g, ['qi']],
  [/shi(?=[aeo])/g, ['xi']],
  [/zi(?=[aeo])/g, ['ji']],
  [/ci(?=[aeo])/g, ['qi']],
  [/si(?=[aeo])/g, ['xi']],
  [/ow/g, ['ao', 'ou']],
  [/oh/g, ['o']],
  [/ah/g, ['a']],
  [/eh/g, ['e']],
  // English "wh" is just /w/ — "when" is the recogniser's spelling of the final -wen.
  [/wh/g, ['w']],
  // The apical vowel after z/c/s/zh/ch/sh/r is not [i]; English ears write it "uh" or "er".
  [/uh/g, ['i', 'e']],
  [/^([zcs]h?|r)er$/g, ['$1i']],
  // A recogniser hears the -ng of a Mandarin final as a bare -n constantly, and vice versa.
  [/on$/g, ['ong', 'uan']],
  [/uen$/g, ['uan', 'un']],
  // "yu"/"yoo"/"you" all reach for ü.
  [/^yoo/g, ['yu']],
  [/^joo/g, ['ju']],
  // Doubled consonants are an English convention and never pinyin.
  [/([bcdfghjklmnpqrstvwxz])\1/g, ['$1']],
  // A trailing silent -e, which in English only ever follows a consonant ("made", "Yue"
  // written "Yuee"). Restricted to that on purpose. Unrestricted it also fired after a
  // vowel, and then a re-glued "leo" + "e" became "liue" and then "liu" — deleting a whole
  // spoken word and scoring a perfect match against the surname of every Liu child on the
  // roster, while the child whose given name accounted for both words scored the same. A
  // rewrite may not make a word disappear.
  [/([a-z]{2,}[bcdfghjklmnpqrstvwxyz])e$/g, ['$1']],
];

/** Beyond this the expansion is costing more than the extra readings are worth. */
const MAX_VARIANTS = 12;

const CACHE = new Map<string, string[]>();
const CACHE_LIMIT = 5_000;

/**
 * The pinyin spellings `word` might be, including the word itself.
 *
 * The word itself is always first and always present — plenty of transcripts already are
 * pinyin ("chen", "ming", "hao"), and a rewrite must never be able to lose the plain reading.
 */
export function pinyinSpellings(word: string): string[] {
  const cached = CACHE.get(word);
  if (cached) return cached;

  let variants = [word];
  for (const [pattern, replacements] of REWRITES) {
    const next = new Set(variants);
    for (const variant of variants) {
      if (!pattern.test(variant)) {
        pattern.lastIndex = 0;
        continue;
      }
      pattern.lastIndex = 0;
      for (const replacement of replacements) {
        next.add(variant.replace(pattern, replacement));
        pattern.lastIndex = 0;
      }
    }
    variants = [...next].slice(0, MAX_VARIANTS);
  }

  const out = variants.filter(Boolean);
  if (CACHE.size >= CACHE_LIMIT) CACHE.clear();
  CACHE.set(word, out);
  return out;
}

/**
 * What the *name* of an English letter sounds like as a pinyin syllable.
 *
 * A recogniser that cannot place a syllable sometimes falls back to spelling: a teacher
 * saying 刘一 gets back "Leo E", where the E is the letter's name standing in for the
 * syllable yi. This has to be applied to whole tokens before the words are re-glued —
 * once "leo" and "e" are joined the E is no longer a token and nothing can see it — so it
 * lives here as data and is applied in `voiceNameMatch`, not as one more spelling rewrite.
 */
export const LETTER_NAME_SYLLABLES: Readonly<Record<string, string>> = {
  a: 'ei', b: 'bi', c: 'xi', d: 'di', e: 'yi', g: 'ji', i: 'ai', j: 'jei',
  k: 'kai', o: 'ou', p: 'pi', q: 'qiu', t: 'ti', u: 'you', v: 'wei', y: 'wai', z: 'zi',
};
